import { getEarningsStateMeta } from "./marketplaceFinanceStates";

export const serviceLifecycleStatuses = {
  start: ["assigned", "en_route", "arrived", "otp_pending_start", "in_progress"],
  completion: [
    "completion_requested",
    "otp_pending_completion",
    "completed",
    "disputed",
    "cancelled",
  ],
};

export function getServiceLifecycleStage(status) {
  if (["completed"].includes(status)) return "Completed";
  if (["disputed"].includes(status)) return "Disputed";
  if (["awaiting_manual_assignment"].includes(status)) {
    return "Manual assignment needed";
  }
  if (["cancelled", "declined"].includes(status)) return "Cancelled";
  if (["completion_requested", "otp_pending_completion"].includes(status)) {
    return "Completion in review";
  }
  if (["assigned", "en_route", "arrived", "otp_pending_start", "in_progress"].includes(status)) {
    return "Execution in progress";
  }
  if (["worker_reassigned"].includes(status)) {
    return "Reassigned";
  }
  if (["accepted", "worker_assigned", "worker_reassigned"].includes(status)) {
    return "Assigned";
  }
  return "Awaiting assignment";
}

export function getOtpStateLabel(booking) {
  if (booking?.status === "otp_pending_start") return "Start OTP pending";
  if (booking?.status === "otp_pending_completion") return "Completion OTP pending";
  if (booking?.service_started_at && booking?.service_completed_at) return "OTP lifecycle completed";
  if (booking?.service_started_at) return "Start OTP verified";
  return "OTP not started";
}

export function getFinanceStateLabel(booking) {
  const timelineState =
    booking?.latestFinanceState ||
    booking?.finance_state ||
    booking?.latest_finance_state;

  if (!timelineState) {
    if (booking?.payment_state === "in_escrow") {
      return "Final checks in progress";
    }

    if (booking?.payment_state === "settled") {
      return "Earnings secured";
    }

    return "Finance state not started";
  }

  return getEarningsStateMeta(timelineState).workerLabel;
}

export function getFinanceStateTone(booking) {
  const timelineState =
    booking?.latestFinanceState ||
    booking?.finance_state ||
    booking?.latest_finance_state;

  if (!timelineState) return "info";
  return getEarningsStateMeta(timelineState).tone;
}

export function getClosureSummary(booking) {
  const finalValue = Number(booking?.final_project_value || 0);
  const collected = Number(booking?.payment_collected || 0);
  const advance = Number(booking?.advance_received || 0);
  const completion = Number(booking?.work_completed_percentage || 0);

  return {
    finalValue,
    collected,
    advance,
    completion,
    hasClosureData:
      finalValue > 0 ||
      collected > 0 ||
      advance > 0 ||
      completion > 0 ||
      !!booking?.closure_summary,
  };
}

export function getCommissionExposure(booking) {
  const finalValue = Number(booking?.final_project_value || 0);
  const expectedCommission = Number(booking?.expected_commission_amount || 0);

  if (expectedCommission > 0) {
    return {
      label: `Tracked commission Rs ${expectedCommission.toLocaleString("en-IN")}`,
      tone: "tracked",
    };
  }

  if (finalValue > 0) {
    return {
      label: "Closure value declared, commission rule pending",
      tone: "watch",
    };
  }

  return {
    label: "Closure value not declared yet",
    tone: "missing",
  };
}

export function getFraudSignals(bookings = []) {
  const suspicious = [];

  bookings.forEach((booking) => {
    const finalValue = Number(booking?.final_project_value || 0);
    const otpFailures = Number(booking?.otp_failure_count || 0);
    const closureEdits = Number(booking?.closure_edit_count || 0);
    const disputes = Number(booking?.completion_dispute_count || 0);

    if (
      ["completion_requested", "otp_pending_completion", "completed"].includes(booking?.status) &&
      finalValue <= 0
    ) {
      suspicious.push({
        bookingId: booking.id,
        label: "Completion flow without final value",
        severity: "high",
      });
    }

    if (otpFailures >= 3) {
      suspicious.push({
        bookingId: booking.id,
        label: "Repeated OTP failures",
        severity: "medium",
      });
    }

    if (closureEdits >= 2) {
      suspicious.push({
        bookingId: booking.id,
        label: "Repeated closure edits",
        severity: "medium",
      });
    }

    if (disputes >= 1) {
      suspicious.push({
        bookingId: booking.id,
        label: "Completion dispute raised",
        severity: "high",
      });
    }
  });

  return suspicious;
}
